import React, { useState, useEffect } from "react";
import { Calendar as ReactCalendar } from "react-calendar";
import "react-calendar/dist/Calendar.css";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import axios from "axios";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import { useSubmitGuard } from "../../utils/useSubmitGuard";
import { API_DETAILS, COOKIE_KEYS } from "../../App";
import Loader from "../../utils/loader";
import { toast } from "../../utils/toast";
import { errorMessage } from "../../utils/errorMessage";
import { clickable } from "../../utils/a11y";
import "../../styles/Patient-ui/Patient-Homepage.css";
import "../../styles/Patient-ui/Patient-Apt-Calender.css";

const PatientAppointment = (props) => {
  const navigate = useNavigate();
  const minDate = new Date();
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 15);

  // ---------- state ----------
  const [subscriptionActive, setSubscriptionActive] = useState(true);
  const [hasEverSubscribed, setHasEverSubscribed] = useState(true);
  const [event, setEvent] = useState(null); // selected slot label e.g. "10:30 AM"
  const [dateSelection, setDateSelection] = useState(new Date());
  const [open, setOpen] = useState(false);

  const [slotsData, setSlotsData] = useState([]);
  // A failed availability lookup used to render as "No slots available on
  // selected date", which reads as a full diary rather than a broken request.
  const [slotsLoadFailed, setSlotsLoadFailed] = useState(false);
  const [slotsOnDate, setSlotsOnDate] = useState(null);
  const [selectedInfo, setSelectedInfo] = useState(null);

  const [viewVal, setViewVal] = useState(0);
  const [diagnosisNames, setDiagnoses] = useState([]);
  const [alreadyBookedOnDate, setAlreadyBookedOnDate] = useState(false);

  // ✅ IMPORTANT: store diagId (string number) NOT name
  const [selectedOption, setSelectedOption] = useState("");

  const [activeLoader, setActiveLoader] = useState(false);
  // Booking twice from a double-click would request the same slot twice.
  const [booking, guard] = useSubmitGuard();
  const [newApptDocName, setNewApptDocName] = useState("");

  // ✅ wizard: 1 date, 2 time, 3 confirm
  const [activeStep, setActiveStep] = useState(1);

  const goToStep = (step) => {
    setActiveStep(step);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSelectChange = (e) => {
    // value is diagId (string)
    setSelectedOption(e.target.value);
  };

  const navToProfile = (viewVal) => props.changeView(viewVal);

  useEffect(() => {
    fetch15DaysSlots();
    fetchAllDiagnoses();
    fetchSubscriptionDetails();

    if (viewVal === 4 || viewVal === 6) navToProfile(viewVal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewVal]);

  // ---------- API ----------
  const fetchSubscriptionDetails = async () => {
    const userId = parseInt(Cookies.get(COOKIE_KEYS.userId), 10);
    if (!userId) return;

    const url =
      API_DETAILS.baseUrl +
      API_DETAILS.PORT +
      API_DETAILS.baseExtension +
      `/getSubscriptionDetails/${userId}`;

    const config = {
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "*",
        "Content-Type": "application/json",
      },
    };

    try {
      const res = await axios.get(url, config);
      setSubscriptionActive(res.data?.isActive !== false);
      // Defaults to true so a missing field reads as "renew" rather than telling
      // an existing member to activate something they already have.
      setHasEverSubscribed(res.data?.hasEverSubscribed !== false);
    } catch (err) {
    }
  };

  const fetchAllDiagnoses = async () => {
    const url =
      API_DETAILS.baseUrl +
      API_DETAILS.PORT +
      API_DETAILS.baseExtension +
      `/getAllDiagnoses`;

    const config = {
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "*",
        "Content-Type": "application/json",
      },
    };

    setActiveLoader(true);
    try {
      const res = await axios.get(url, config);
      setDiagnoses(res.data || []);
    } catch (err) {
    } finally {
      setActiveLoader(false);
    }
  };

  // ✅ This endpoint returns NAME, we map NAME -> diagId
  const fetchLatestDiagnosis = async (patientId) => {
    const url =
      API_DETAILS.baseUrl +
      API_DETAILS.PORT +
      API_DETAILS.baseExtension +
      `/latest-diagnosis-name?patientId=${patientId}`;

    const config = {
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "*",
        "Content-Type": "application/json",
      },
    };

    setActiveLoader(true);
    try {
      const res = await axios.get(url, config);
      const latestName = res.data; // likely string like "Fever"

      if (!latestName) {
        setSelectedOption("");
        return;
      }

      const match = diagnosisNames.find((d) => d.name === latestName);
      if (match) setSelectedOption(String(match.diagId));
      else setSelectedOption(""); // force manual selection if no match
    } catch (err) {
      setSelectedOption("");
    } finally {
      setActiveLoader(false);
    }
  };

  const fetch15DaysSlots = async () => {
    const url =
      API_DETAILS.baseUrl +
      API_DETAILS.PORT +
      API_DETAILS.baseExtension +
      `/getAvailableSlotsByDate?timeZone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`;

    const config = {
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "*",
        "Content-Type": "application/json",
      },
    };

    try {
      const res = await axios.get(url, config);
      const data = res.data || [];
      setSlotsData(data);

      // ✅ preload today's slots but keep step 1 (calendar)
      const today = new Date();
      setDateSelection(today);
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
        today.getDate()
      ).padStart(2, "0")}`;
      getSlots(todayStr, data);

      setActiveStep(1);
      setEvent(null);
      setSelectedInfo(null);

      setSlotsLoadFailed(false);
    } catch (err) {
      setSlotsLoadFailed(true);
    }
  };

  const checkSlotAvailability = async (selectedDate, patId) => {
    const url =
      API_DETAILS.baseUrl +
      API_DETAILS.PORT +
      API_DETAILS.baseExtension +
      `/isSlotBooked?date=${selectedDate}&timeZone=${Intl.DateTimeFormat().resolvedOptions().timeZone}&patId=${patId}`;

    const config = {
      headers: {
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Methods": "*",
        "Content-Type": "application/json",
      },
    };

    try {
      const res = await axios.get(url, config);
      // One appointment per patient per day — the backend counts this patient's
      // scheduled appointments on the date and returns true if any exist. Held in
      // state rather than written onto the DOM directly, so React re-renders do
      // not wipe the styling and the reason can be shown to the patient.
      setAlreadyBookedOnDate(Boolean(res.data));
    } catch (error) {
    }
  };

  // Meeting links used to be made here, after booking: this browser asked for the
  // appointment's details, asked Google for a link, and saved whatever link it
  // had. So the address a provider's "Join Now" opened was whatever this request
  // carried. The server now creates the link as part of booking.

  // ---------- booking ----------
const saveNewAppointment = async (diagIdNum) => {
  const url =
    API_DETAILS.baseUrl +
    API_DETAILS.PORT +
    API_DETAILS.baseExtension +
    `/saveAppointment`;

  setActiveLoader(true);

  try {
    const temp = {
      diagId: diagIdNum, // ✅ always numeric
      docId: Number(selectedInfo.doctorIds[0]), // ✅ make sure it’s int
      patId: parseInt(Cookies.get(COOKIE_KEYS.userId), 10),
      startTime: selectedInfo.startTime + "",
    };

    const res = await axios.post(url, temp);
    setNewApptDocName(res.data.message);
    setOpen(true);

    return true; // ✅ success
  } catch (err) {

    const data = err?.response?.data;
    const status = err?.response?.status;

    // ✅ Extract backend message safely
    const serverMsg =
      (typeof data === "string" && data) ||
      data?.message ||
      data?.detail ||
      data?.description ||
      "Booking failed";

    // ✅ Subscription missing case (your backend Optional.get() empty)
    const isNoActiveSub =
      status === 500 &&
      (serverMsg?.toLowerCase().includes("no value present") ||
        data?.detail?.toLowerCase?.().includes("no value present") ||
        data?.description?.toLowerCase?.().includes("no value present"));

    if (isNoActiveSub) {
      toast.error(
        "You don't have an active membership. Start one to book an appointment."
      );
      return false;
    }

    // The raw server message used to be shown as-is, which put Java internals
    // ("No value present") in front of patients. errorMessage passes through
    // messages written for people and falls back to this sentence otherwise.
    toast.error(errorMessage(err, "We couldn't book that appointment. Try again in a moment."));
    return false;
  } finally {
    setActiveLoader(false);
  }
};

const handleBook = guard(async () => {
  const diagIdNum = Number(selectedOption);

  if (!selectedOption || Number.isNaN(diagIdNum)) {
    toast.error("Choose which condition this appointment is for.");
    return;
  }
  if (!selectedInfo?.doctorIds?.[0] || !selectedInfo?.startTime || !event) {
    toast.error("Choose an available time before booking.");
    return;
  }

  // The meeting link is created by the server as part of booking.
  await saveNewAppointment(diagIdNum);
});


  // ---------- time helpers ----------
  const getEndTime = (time) => {
    let [hour, minutesPeriod] = time.split(":");
    let minutes = minutesPeriod.substring(0, 2);
    let period = minutesPeriod.substring(3);
    hour = parseInt(hour, 10);

    if (minutes === "30") {
      hour += 1;
      minutes = "00";
    } else {
      minutes = "30";
    }

    if (hour === 12 && minutes === "00") {
      period = period === "AM" ? "PM" : "AM";
    }

    if (hour > 12) hour -= 12;
    return `${hour}:${minutes} ${period}`;
  };

  const getSlots = (dateStr, data) => {
    const list = data || slotsData;
    let found = null;

    for (let i = 0; i < list.length; i++) {
      if (list[i].date === dateStr) {
        found = list[i];
        break;
      }
    }
    setSlotsOnDate(found);
  };

  // ✅ Step 1 -> Step 2
  const handleDateSelection = (selectedDateObj, data = null) => {
    setDateSelection(selectedDateObj);

    const dateStr = `${selectedDateObj.getFullYear()}-${String(selectedDateObj.getMonth() + 1).padStart(2, "0")}-${String(
      selectedDateObj.getDate()
    ).padStart(2, "0")}`;

    const patId = parseInt(Cookies.get(COOKIE_KEYS.userId));
    checkSlotAvailability(dateStr, patId);

    // reset slot selection
    setEvent(null);
    setSelectedInfo(null);

    // load slots for date
    getSlots(dateStr, data);

    // if diagnoses already loaded, try mapping latest diagnosis -> diagId
    if (diagnosisNames.length > 0) {
      fetchLatestDiagnosis(patId);
    } else {
      // keep empty until user picks manually
      setSelectedOption("");
    }

    goToStep(2);
  };

  // when diagnoses arrive, try to map latest diagnosis once (nice UX)
  useEffect(() => {
    if (diagnosisNames.length === 0) return;
    const patId = parseInt(Cookies.get(COOKIE_KEYS.userId));
    if (patId) fetchLatestDiagnosis(patId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagnosisNames]);

  // ✅ Step 2 -> Step 3
  const handleSelectSlot = (startTimeLabel, info) => {
    setEvent(startTimeLabel);
    setSelectedInfo(info);
    goToStep(3);
  };

  const getStepStyles = (stepNumber) => {
    const isActive = activeStep === stepNumber;
    const isCompleted = activeStep > stepNumber;
    return {
      circle: {
        width: 32,
        height: 32,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        fontWeight: 600,
        backgroundColor: isActive ? "#a5cf98" : isCompleted ? "#d6e6cf" : "#e6e1d9",
        color: isActive || isCompleted ? "#1f2b18" : "#6b6457",
      },
      label: {
        marginTop: 6,
        fontSize: 14,
        fontWeight: isActive ? 600 : 400,
        color: isActive ? "#222" : "#555",
      },
    };
  };
  const titles = {
      0: "Home",
      1: "Appointments",
      2: "Messaging",
      3: "Education",
      5: "Prescriptions",
  };

  // ---------- UI ----------
  return (
    <div style={{ display: "flex", flexDirection: "column", width: "100%", backgroundColor: "f6f3ed" }} className="whole-container">

      <div style={{ margin: "0 28px", paddingBottom: "40px" }}>
        <p style={{ margin: "0 0 8px", color: "black", fontSize: "28px" }} className="schedule-appt-header">
          Schedule Appointment
        </p>
        <p style={{ marginTop: 0, marginBottom: "24px", color: "#555", fontSize: "14px" }}>
          Choose a date and time for your consultation
        </p>

        {/* Stepper */}
        <div
          className="stepper-container"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "24px",
            marginBottom: "24px",
          }}
        >
          {(() => {
            const styles = getStepStyles(1);
            return (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={styles.circle}>1</div>
                <div style={styles.label}>Select Date</div>
              </div>
            );
          })()}

          <div style={{ flexGrow: 1, height: 1, backgroundColor: "#ddd" }} />

          {(() => {
            const styles = getStepStyles(2);
            return (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={styles.circle}>2</div>
                <div style={styles.label}>Select Time</div>
              </div>
            );
          })()}

          <div style={{ flexGrow: 1, height: 1, backgroundColor: "#ddd" }} />

          {(() => {
            const styles = getStepStyles(3);
            return (
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={styles.circle}>3</div>
                <div style={styles.label}>Confirm</div>
              </div>
            );
          })()}
        </div>

        {/* Wizard steps */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* STEP 1 */}
          {activeStep === 1 && (
            <div
              className="react-calendar-container"
              style={{
                backgroundColor: "#fff",
                borderRadius: "16px",
                padding: "20px 24px",
                minHeight: "500px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
              }}
            >
              <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#333" }}>Select Date</h3>
                <span style={{ fontSize: 13, color: "#777" }}>Choose from available dates</span>
              </div>

              {!subscriptionActive && (
                <button
                  type="button"
                  onClick={() => navigate("/pricing")}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    marginBottom: 16,
                    padding: "14px 16px",
                    borderRadius: 10,
                    border: "1px solid var(--youro-sage)",
                    backgroundColor: "var(--youro-mint)",
                    color: "var(--youro-charcoal)",
                    fontSize: 14,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  Active subscription needed to schedule an appointment.{" "}
                  <span style={{ textDecoration: "underline", fontWeight: 600 }}>
                    {/* "Renew" only makes sense to someone who has subscribed
                        before; a new patient is activating, not renewing. */}
                    {hasEverSubscribed ? "Renew now →" : "Activate now →"}
                  </span>
                </button>
              )}

              {/* showNeighboringMonth must stay on for the weekday columns to
                  line up. The day tiles are laid out with CSS Grid, which
                  auto-places the first tile in column 1 and ignores the inline
                  offset react-calendar uses to position the 1st of the month —
                  with it off, September 2026 (a Tuesday start) shifted two
                  columns and Monday the 7th showed under Saturday. The adjacent
                  days fill the leading cells; Patient-Apt-Calender.css dims them
                  and makes them unclickable.

                  calendarType is "gregory"; "US" is the v3 spelling, still
                  mapped in react-calendar 4 but deprecated. */}
              <ReactCalendar
                className="custom-react-calendar"
                minDate={minDate}
                maxDate={maxDate}
                onChange={(d) => handleDateSelection(d, null)}
                value={dateSelection}
                calendarType="gregory"
                locale="en-US"
                showNeighboringMonth={true}
                tileClassName={({ date, view }) => {
                  if (view !== "month") return "";

                  const pad = (n) => String(n).padStart(2, "0");
                  const key = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

                  // find slots for this day
                  const dayObj = slotsData?.find((x) => x.date === key);
                  const hasAppt = (dayObj?.noOfSlots ?? 0) > 0;

                  // compare day vs today (midnight)
                  const today = new Date();
                  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

                  let apptClass = "no-appt";
                  if (hasAppt) {
                    if (dayStart.getTime() === todayStart.getTime()) apptClass = "has-appt-today";
                    else if (dayStart.getTime() < todayStart.getTime()) apptClass = "has-appt-past";
                    else apptClass = "has-appt-future";
                  }

                  const isSelected =
                        dateSelection &&
                        date.getFullYear() === dateSelection.getFullYear() &&
                        date.getMonth() === dateSelection.getMonth() &&
                        date.getDate() === dateSelection.getDate();

                  return `cal-tile ${apptClass} ${isSelected ? "cal-selected" : ""}`;
                }}
              />
            </div>
          )}

          {/* STEP 2 */}
          {activeStep === 2 && (
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: 16,
                padding: "26px 28px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                border: "1px solid rgba(0,0,0,0.06)",
                width: "100%",

                // ✅ FIXED height box (like step 3)
                height: "500px",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* Header Row: title left, back right */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  marginBottom: 18,
                  flexShrink: 0,
                }}
              >
                <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#222" }}>
                    Available Times
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 500, color: "#7a7166" }}>
                    -{" "}
                    {dateSelection.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </div>
                </div>

                {/* ✅ Back button styled SAME as Step 3 back */}
                <button
                  type="button"
                  onClick={() => goToStep(1)}
                  style={{
                    height: 46,
                    padding: "0 18px",
                    borderRadius: 14,
                    border: "2px solid #2b7aa6",
                    backgroundColor: "#fff",
                    color: "#2b7aa6",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: "pointer",
                    transition: "all 0.18s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "rgba(43, 122, 166, 0.06)";
                    e.currentTarget.style.transform = "scale(1.01)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#fff";
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  ← Back
                </button>
              </div>

              {alreadyBookedOnDate && (
                <div
                  style={{
                    flexShrink: 0,
                    marginBottom: 16,
                    padding: "14px 16px",
                    borderRadius: 10,
                    border: "1px solid var(--youro-sage)",
                    backgroundColor: "var(--youro-mint)",
                    color: "var(--youro-charcoal)",
                    fontSize: 14,
                  }}
                >
                  You already have an appointment booked on this day. Youro allows
                  one appointment per day — pick another date, or cancel the
                  existing appointment from your home page first.
                </div>
              )}

              {/* ✅ ONLY this section scrolls */}
              <div
                style={{
                  flex: 1,
                  overflowY: "auto",
                  paddingRight: 6,
                }}
              >
                <div
                  style={{
                    display: "grid",

                    // ✅ bigger slot cards
                    //
                    // min() so the 260px is a preference rather than a floor.
                    // A bare minmax(260px, 1fr) keeps the column at 260px even
                    // when the container is narrower, so on a phone — where
                    // this pane is about 200px — every time slot ran off the
                    // right edge and was clipped. Identical above 260px.
                    gridTemplateColumns: "repeat(auto-fit, minmax(min(260px, 100%), 1fr))",

                    // ✅ smaller gaps
                    gap: 12,

                    paddingBottom: 8,
                  }}
                >
                  {slotsOnDate &&
                    slotsOnDate.slotInfo
                      .filter((slot) => new Date(slot.startTime).getTime() >= Date.now())
                      .map((slot) => {
                        const dateObj = new Date(slot.startTime);
                        let hours = dateObj.getHours();
                        const minutes = String(dateObj.getMinutes()).padStart(2, "0");
                        const period = hours < 12 ? "AM" : "PM";
                        hours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;

                        const startTimeLabel = `${String(hours).padStart(2, "0")}:${minutes} ${period}`;
                        const isSelected = event === startTimeLabel;

                        const cardStyle = {
                          borderRadius: 16,
                          border: isSelected ? "2px solid #8fa77d" : "1px solid #e7e0d7",
                          backgroundColor: alreadyBookedOnDate
                            ? "#e0e0e0"
                            : isSelected
                            ? "#d7ead7"
                            : "#ffffff",
                          color: alreadyBookedOnDate ? "#a0a0a0" : undefined,

                          // ✅ bigger height/width feel
                          height: 88,

                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 14,
                          cursor: alreadyBookedOnDate ? "not-allowed" : "pointer",
                          pointerEvents: alreadyBookedOnDate ? "none" : "auto",
                          transition: "all 0.18s ease",
                          boxShadow: isSelected ? "0 6px 18px rgba(0,0,0,0.06)" : "none",
                        };

                        return (
                          <div
                            key={slot.startTime}
                            className="slot-timings"
                            {...clickable(() => {
                              if (alreadyBookedOnDate) return;
                              handleSelectSlot(startTimeLabel, slot);
                            }, `Book ${startTimeLabel}`)}
                            aria-pressed={isSelected}
                            aria-disabled={alreadyBookedOnDate}
                            style={cardStyle}
                            onMouseEnter={(e) => {
                              if (isSelected || alreadyBookedOnDate) return;
                              e.currentTarget.style.backgroundColor = "rgba(156, 177, 137, 0.18)";
                              e.currentTarget.style.borderColor = "#b9d9b9";
                            }}
                            onMouseLeave={(e) => {
                              if (isSelected || alreadyBookedOnDate) return;
                              e.currentTarget.style.backgroundColor = "#ffffff";
                              e.currentTarget.style.borderColor = "#e7e0d7";
                            }}
                          >
                            {/* Clock icon */}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="26"
                              height="26"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#2d2a26"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              style={{ opacity: isSelected ? 1 : 0.9 }}
                            >
                              <circle cx="12" cy="12" r="9"></circle>
                              <path d="M12 7v5l3 2"></path>
                            </svg>

                            <div style={{ fontSize: 18, fontWeight: 300, color: "#2d2a26" }}>
                              {startTimeLabel}
                            </div>
                          </div>
                        );
                      })}

                  {!slotsOnDate && slotsLoadFailed && (
                    <div style={{ gridColumn: "1 / -1", color: "var(--youro-error)", fontSize: 14 }}>
                      We couldn't load available times. Try again in a moment — this doesn't mean the day is full.
                    </div>
                  )}

                  {!slotsOnDate && !slotsLoadFailed && (
                    <div style={{ gridColumn: "1 / -1", color: "var(--youro-muted)", fontSize: 14 }}>
                      No slots available on selected date. Choose another date.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 */}
          {activeStep === 3 && (
            <div
              style={{
                backgroundColor: "#fff",
                borderRadius: 16,
                padding: "26px 28px",
                boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
                border: "1px solid rgba(0,0,0,0.06)",
                width: "100%",
              }}
            >
              {/* Header */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#222" }}>
                  Confirm Appointment
                </div>
                <div style={{ marginTop: 6, fontSize: 14, color: "#6f675c" }}>
                  Review your appointment details
                </div>
              </div>

              {/* Diagnosis (keep linear, clean, not dominant) */}
              <div style={{ marginBottom: 18 }}>
                <label
                  htmlFor="d"
                  style={{
                    display: "block",
                    marginBottom: 8,
                    fontSize: 13,
                    color: "#6f675c",
                    fontWeight: 600,
                  }}
                >
                  Diagnosis
                </label>

                <select
                  id="d"
                  name="d"
                  value={selectedOption}
                  onChange={handleSelectChange}
                  style={{
                    width: "100%",
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: "1px solid #e6e1d9",
                    backgroundColor: "#fff",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#2d2a26",
                  }}
                >
                  <option value="">Select Diagnosis</option>
                  {diagnosisNames
                    /* "Intake Form" is a questionnaire record that shares the
                       diagnosis table; it drives the intake flow and is not a
                       condition a patient can book an appointment for. */
                    .filter((result) => result.name?.trim().toLowerCase() !== "intake form")
                    .map((result) => (
                      <option key={result.diagId} value={String(result.diagId)}>
                        {result.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* If no slot selected, show gentle message */}
              {!event || !selectedInfo ? (
                <div style={{ padding: "18px 0", fontSize: 14, color: "#6f675c" }}>
                  Please go back and select a time to continue.
                </div>
              ) : (
                <>
                  {/* Summary grid (matches expected layout) */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      columnGap: 40,
                      rowGap: 26,
                      paddingTop: 8,
                      paddingBottom: 18,
                    }}
                  >
                    {/* Date */}
                    <div>
                      <div style={{ fontSize: 13, color: "#6f675c", fontWeight: 600 }}>
                        Date
                      </div>
                      <div style={{ marginTop: 6, fontSize: 16, fontWeight: 700, color: "#2d2a26" }}>
                        {dateSelection.toLocaleDateString("en-US", {
                          weekday: "long",
                          month: "long",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </div>
                    </div>

                    {/* Time */}
                    <div>
                      <div style={{ fontSize: 13, color: "#6f675c", fontWeight: 600 }}>
                        Time
                      </div>
                      <div style={{ marginTop: 6, fontSize: 16, fontWeight: 700, color: "#2d2a26" }}>
                        {event}
                      </div>
                    </div>

                    {/* Type */}
                    <div>
                      <div style={{ fontSize: 13, color: "#6f675c", fontWeight: 600 }}>
                        Type
                      </div>
                      <div style={{ marginTop: 6, fontSize: 16, fontWeight: 700, color: "#2d2a26" }}>
                        Video Consultation
                      </div>
                    </div>

                    {/* No Duration cell. A visit takes as long as it takes, and
                        stating a fixed 30 minutes set an expectation the practice
                        does not intend to meet either way — a patient who needs
                        longer should not feel they are running over, and one who
                        needs less should not feel short-changed. Booking still
                        reserves a 30-minute slot; that is scheduling, not a
                        promise to the patient. Removed from the mobile summary at
                        the same time.

                        Three cells in a two-column grid on purpose: Date and Time
                        pair on the first row, Type sits under them. */}
                  </div>

                  {/* Green info banner */}
                  <div
                    style={{
                      marginTop: 6,
                      backgroundColor: "#d7ead7",
                      borderRadius: 14,
                      padding: "18px 18px",
                      color: "#2d2a26",
                      fontSize: 14,
                      fontWeight: 500,
                    }}
                  >
                    A calendar invite and reminder will be sent to your email.
                  </div>

                  {/* Buttons row */}
                  <div
                    style={{
                      marginTop: 22,
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 22,
                    }}
                  >
                    {/* Back button - styled like your time-slot style (bordered pill/card) */}
                    <button
                      type="button"
                      onClick={() => goToStep(2)}
                      style={{
                        height: 56,
                        borderRadius: 14,
                        border: "2px solid #2b7aa6", // similar to expected blue border
                        backgroundColor: "#fff",
                        color: "#2b7aa6",
                        fontSize: 18,
                        fontWeight: 700,
                        cursor: "pointer",
                        transition: "all 0.18s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "rgba(43, 122, 166, 0.06)";
                        e.currentTarget.style.transform = "scale(1.01)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "#fff";
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      Back
                    </button>

                    {/* Confirm button */}
                    <button
                      type="button"
                      disabled={booking || !selectedOption || Number.isNaN(Number(selectedOption))}
                      aria-busy={booking}
                      onClick={handleBook}
                      style={{
                        height: 56,
                        borderRadius: 14,
                        border: "none",
                        backgroundColor:
                          !selectedOption || Number.isNaN(Number(selectedOption))
                            ? "#f3b1a6"
                            : "#ff6f61",
                        color: "#fff",
                        fontSize: 18,
                        fontWeight: 800,
                        cursor:
                          !selectedOption || Number.isNaN(Number(selectedOption))
                            ? "not-allowed"
                            : "pointer",
                        transition: "all 0.18s ease",
                        opacity:
                          !selectedOption || Number.isNaN(Number(selectedOption))
                            ? 0.7
                            : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (!selectedOption || Number.isNaN(Number(selectedOption))) return;
                        e.currentTarget.style.transform = "scale(1.01)";
                        e.currentTarget.style.filter = "brightness(0.98)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = "scale(1)";
                        e.currentTarget.style.filter = "none";
                      }}
                    >
                      Confirm Booking
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Popup */}
        <div className="popup-after-booking">
          <Popup
            open={open}
            modal
            closeOnDocumentClick={false}
            onClose={() => setOpen(false)}
            contentStyle={{ padding: 0, border: "none", background: "transparent" }}
            overlayStyle={{ background: "rgba(0,0,0,0.45)" }}
          >
            <div className="apt-modal">
              {/* Close */}
              <button
                className="apt-modal-close"
                type="button"
                onClick={() => {
                  setOpen(false);
                  props.changeView(0);
                }}
                aria-label="Close"
              >
                ×
              </button>

              {/* Icon */}
              <div className="apt-modal-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="42"
                  height="42"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </div>

              {/* Title + message */}
              <div className="apt-modal-title">Appointment Confirmed!</div>
              <div className="apt-modal-sub">
                Your appointment has been successfully booked. A confirmation email with calendar
                invite has been sent to you.
              </div>

              {/* Details card */}
              <div className="apt-modal-details">
                <div className="apt-modal-details-title">Appointment Details:</div>

                <div className="apt-modal-details-row">
                  <div>
                    {dateSelection.toLocaleDateString("en-US", {
                      weekday: "long",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                  <div>{event}</div>
                </div>
              </div>
            </div>
          </Popup>
        </div>
      </div>

      <Loader active={activeLoader} />
    </div>
  );
};

export default PatientAppointment;
